/**
 * DenseSection — F-003.3 Phase 1 (IMPL-P1-003).
 * AccordionItem con completeness indicator (check/warning/empty).
 * forceMount: i children rimangono montati anche se l'accordion e chiuso,
 * preservando i valori RHF nell'albero React.
 */
import { useFormContext } from 'react-hook-form';
import type { ContractDraft } from '@app/shared';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { CheckCircle2, AlertCircle, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

type CompletenessState = 'complete' | 'error' | 'empty';

export interface DenseSectionProps {
  id: string;
  title: string;
  fields: ReadonlyArray<string>;
  children: React.ReactNode;
  isOpen: boolean;
}

function getCompleteness(
  fields: ReadonlyArray<string>,
  values: Record<string, unknown>,
  errors: Record<string, unknown>,
): CompletenessState {
  // Check if any field has an error
  const hasErrors = fields.some((f) => {
    const parts = f.split('.');
    let cursor: unknown = errors;
    for (const p of parts) {
      if (cursor == null || typeof cursor !== 'object') return false;
      cursor = (cursor as Record<string, unknown>)[p];
    }
    return cursor != null;
  });
  if (hasErrors) return 'error';

  // Check if any field has a value
  const hasValues = fields.some((f) => {
    const parts = f.split('.');
    let cursor: unknown = values;
    for (const p of parts) {
      if (cursor == null || typeof cursor !== 'object') return false;
      cursor = (cursor as Record<string, unknown>)[p];
    }
    return cursor != null && cursor !== '';
  });
  if (hasValues) return 'complete';
  return 'empty';
}

export function DenseSection({ id, title, fields, children, isOpen }: DenseSectionProps) {
  const { formState, getValues } = useFormContext<ContractDraft>();
  const values = getValues() as Record<string, unknown>;
  const errors = formState.errors as Record<string, unknown>;
  const state = getCompleteness(fields, values, errors);

  const icon =
    state === 'complete' ? (
      <CheckCircle2
        className="h-4 w-4 text-green-600"
        aria-hidden="true"
      />
    ) : state === 'error' ? (
      <AlertCircle
        className="h-4 w-4 text-orange-500"
        aria-hidden="true"
      />
    ) : (
      <Circle
        className="h-4 w-4 text-muted-foreground"
        aria-hidden="true"
      />
    );

  const ariaLabel =
    state === 'complete'
      ? `${title}: completo`
      : state === 'error'
        ? `${title}: errori presenti`
        : `${title}: da compilare`;

  return (
    <AccordionItem value={id} id={`section-${id}`}>
      <AccordionTrigger
        id={`section-${id}-trigger`}
        className="hover:no-underline"
        aria-label={ariaLabel}
      >
        <span className="flex items-center gap-2">
          {icon}
          <span>{title}</span>
        </span>
      </AccordionTrigger>
      {/* forceMount: mantiene i children nel React tree -> RHF state preservato */}
      <AccordionContent
        forceMount
        className={cn('pb-4', !isOpen && 'hidden')}
      >
        {children}
      </AccordionContent>
    </AccordionItem>
  );
}
